import React, { useEffect, useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import {
  SelectInput,
  useTranslate,
  useNotify,
  required,
  FormDataConsumer,
  NumberInput,
  minValue,
} from 'react-admin'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { Delete, Add } from '@mui/icons-material'
import Button from '@mui/material/Button'
import { Grid, Skeleton } from '@mui/material'
import { removeCurrenciesByIds } from 'src/components/pages/administration/helper'
import { ConfirmationDialog } from 'src/components/uiElements/styledComponents/ConfirmationDialog'
export const FieldArray = ({ editMode = false }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { control } = useFormContext()
  const [optionsCurrency, setOptionsCurrency] = useState([])
  const [loading, setLoading] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(null)
  const { fields, append, remove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'currency_relation', // unique name for your Field Array
  })

  const getAllCurrencys = async () => {
    setLoading(true)
    try {
      const allCurrencys = await currencyServices.getByAll()
      if (allCurrencys) {
        setOptionsCurrency(allCurrencys)
        setLoading(false)
        return
      }
      setOptionsCurrency([])
      notify(`Currencys not found`, { type: 'error' })
      setLoading(false)
    } catch (error) {
      setOptionsCurrency([])
      notify(`please, refresh page`, { type: 'error' })
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      await getAllCurrencys()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (confirmDelete) {
      removeCurrenciesByIds(currentIndex, fields, remove, editMode)
      setOpenModal(false)
      setCurrentIndex(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete])

  const deleteCurrency = (originalIndex, currency, remove, editMode) => {
    if (editMode && !('new_register' in currency)) {
      setOpenModal(true)
      setCurrentIndex(originalIndex)
      return
    }
    remove(originalIndex)
  }

  return (
    <>
      <ConfirmationDialog
        title={translate('ra.message.delete_currency')}
        buttonConfirmText={translate('ra.action.to_confirm')}
        buttonCancel={translate('ra.action.cancel')}
        setOpen={setOpenModal}
        setConfirm={setConfirmDelete}
        open={openModal}
      />
      {React.Children.toArray(
        fields
          .map((field, index) => ({ field, originalIndex: index })) // Mapea los fields a un nuevo array que incluye el field y su índice original.
          .filter(({ field }) => !field.is_reverse) // Filtra los fields como antes.
          .map(({ field, originalIndex }, index) => (
            <div
              key={field.id}
              style={{
                width: '100%',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Grid container spacing={3}>
                <EditInputsWrap label={translate('ra.field.currency_from')} required>
                  {loading ? (
                    <Skeleton variant='rectangular' width={'100%'} height={40} />
                  ) : (
                    <SelectInput
                      source={`currency_relation.${originalIndex}.currency_id`}
                      optionText='long_name'
                      fullWidth
                      choices={optionsCurrency}
                      label=''
                      validate={required()}
                      disabled={field?.is_reverse || (editMode && !('new_register' in field))}
                    />
                  )}
                </EditInputsWrap>
                <FormDataConsumer>
                  {({ formData }) => {
                    const currentCurrencyId = formData.currency_relation?.[originalIndex]?.currency_id
                    const currentCurrencyToId = formData.currency_relation?.[originalIndex]?.currency_to_id

                    // Recolecta todos los currency_to_id de los registros que tienen el mismo currency_id
                    const excludeCurrencyToIds = formData.currency_relation
                      .filter((relation) => relation.currency_id === currentCurrencyId)
                      .map((relation) => relation.currency_to_id)

                    // Recolecta todos los currency_id de los registros que tienen el mismo currency_to_id
                    const excludeCurrencyIds = formData.currency_relation
                      .filter((relation) => relation.currency_to_id === currentCurrencyId)
                      .map((relation) => relation.currency_id)

                    // Aplica el filtro a las opciones
                    const options = optionsCurrency.filter((option) => {
                      // No excluye la opción si es la misma que el usuario acaba de seleccionar
                      if (option.id === currentCurrencyToId) {
                        return true
                      }

                      // Condición 1: Excluir la opción si es igual al currentCurrencyId
                      if (option.id === currentCurrencyId) {
                        return false
                      }

                      // Condición 2: Excluir la opción si está en excludeCurrencyToIds
                      if (excludeCurrencyToIds.includes(option.id)) {
                        return false
                      }

                      // Condición 3: Excluir la opción si está en excludeCurrencyIds
                      if (excludeCurrencyIds.includes(option.id)) {
                        return false
                      }

                      return true
                    })
                    return (
                      <EditInputsWrap label={translate('ra.field.currency_to')} required>
                        {loading ? (
                          <Skeleton variant='rectangular' width={'100%'} height={40} />
                        ) : (
                          <SelectInput
                            source={`currency_relation.${originalIndex}.currency_to_id`}
                            optionText='long_name'
                            fullWidth
                            choices={editMode && !('new_register' in field) ? optionsCurrency : options}
                            label={false}
                            disabled={
                              field?.is_reverse ||
                              (editMode && !('new_register' in field)) ||
                              !currentCurrencyId
                            }
                            validate={required()}
                          />
                        )}
                      </EditInputsWrap>
                    )
                  }}
                </FormDataConsumer>
                <EditInputsWrap label={translate('ra.field.value')}>
                  <NumberInput
                    fullWidth
                    source={`currency_relation.${originalIndex}.value`}
                    label=''
                    defaultValue={1}
                    validate={[minValue(0.00001)]}
                  />
                </EditInputsWrap>
              </Grid>
              <Grid container spacing={3} justifyContent='flex-end' className='addItemSeparator'>
                <Grid item xs={12} sm={12} className='grid'>
                  <Button
                    onClick={() => deleteCurrency(originalIndex, field, remove, editMode)}
                    startIcon={<Delete />}
                    color='error'
                  >
                    {translate('ra.action.delete')}
                  </Button>
                </Grid>
              </Grid>
            </div>
          ))
      )}
      <Button
        startIcon={<Add />}
        onClick={() => {
          append({
            currency_id: null,
            currency_to_id: null,
            value: null,
            is_reverse: false,
            new_register: true,
          })
        }}
      >
        {translate('ra.action.add_specific', {
          name: translate('ra.field.currency'),
        })}
      </Button>
    </>
  )
}
